import React from 'react';

import { CalendarDateService } from '@uplift/services';
import { useTimezone } from '@uplift/contexts';
import { useFormSubmissionResultsQuery } from '@uplift/queries';


export const useFormResponsesReviewData = (submissionId: string) => {
  const timezone = useTimezone();

  const {
    data: formReviewData,
  } = useFormSubmissionResultsQuery(submissionId, { suspense: true });

  const groups = React.useMemo(() => {
    if (!formReviewData) {
      return {};
    }
    return formReviewData.groups.reduce<Record<string, string>>((prev, curr) => ({
      ...prev,
      [curr.formGroupId]: curr.name,
    }), { _other: 'Other' });
  }, [ formReviewData ]);

  const answers = React.useMemo(() => {
    if (!formReviewData) {
      return {};
    }

    type Answer = {
      elementId: string;
      answerIds?: string[];
      text?: string;
    };

    return formReviewData.responses.responses.map<Answer>(response => ({
      elementId: response.formElementId,
      answerIds: response.formElementAnswerId?.split('|'),
      text: response.responseText,
    })).reduce<Record<string, Answer>>((prev, curr) => ({
      ...prev,
      [curr.elementId]: curr
    }), {});
  }, [ formReviewData ]);

  const questions = React.useMemo(() => {
    if (!formReviewData) {
      return [];
    }

    return formReviewData.elements.map(element => {
      const result = {
        id: element.formElementId,
        groupId: element.formGroupId,
        title: element.elementText,
        type: element.formElementDefinition.formElementType,
        order: element.orderNumber,
      };

      if ([ 'simple_input', 'short_answer' ].includes(element.formElementDefinition.formElementType)) {
        return {
          ...result,
          value: answers?.[element.formElementId]?.text
        };
      }

      if (element.formElementDefinition.formElementType === 'page') {
        return undefined;
      }

      const formElementAnswers = element.formElementAnswers.reduce<Record<string, string>>((prev, curr) => ({
        ...prev,
        [curr.formElementAnswerId]: curr.answerText,
      }), {});

      // TODO: handle it correctly, because this is possible to be undefined
      const values: string[] = answers[element.formElementId]?.answerIds?.map(a => formElementAnswers[a]) as string[];

      return {
        ...result,
        value: values,
      };
    }).filter(i => i?.value !== undefined && i?.value !== null);
  }, [ formReviewData, answers ]);

  const grouped = React.useMemo(() => {
    const groupedItems: Record<string, { title: string; items: typeof questions }> = {};

    questions.forEach(q => {
      const groupId = q?.groupId || '_other';

      if (!groupedItems[groupId]) {
        groupedItems[groupId] = {
          title: groups[groupId],
          items: [],
        };
      }

      groupedItems[groupId].items.push(q);
    });

    const groupsArray = Object.keys(groupedItems).map(key => groupedItems[key]);

    groupsArray.sort((a, b) => {
      if (!a.items[0] || !b.items[0]) return 0;

      return a.items[0]?.order - b.items[0]?.order;
    });

    return groupsArray;
  }, [ questions, groups ]);

  const completedAt = React.useMemo(() => {
    if (formReviewData?.responses?.complete === true) {
      return CalendarDateService.date(formReviewData.responses.updatedAt).tz(timezone);
    } else if (formReviewData?.responses?.complete === false) {
      return undefined;
    }
    return null;
  }, [ formReviewData, timezone ]);

  return {
    title: formReviewData?.title,
    groups: grouped,
    completedAt: completedAt,
  };
};
